import React, { createContext, ReactNode, useContext, useState } from "react";
import HomeProvider from "./HomeProvider";
import { BranchInfo } from "../../model/home/BranchInfoModel";
import CacheStorage from "../../service/storage/CacheStorage";
import StorageKeys from "../../const/StorageKeys";
import UtilityHelper from "../../service/utilities/UtilityHelper";
import SessionStorage from "../../service/storage/SessionStorage";
import Loader from "../../widget/common/ui/Loader";
import { AlertConfirm } from "../../widget/common/ui/Alerts";
import { appRoutes } from "../../routes/routes";

type HomeContextType = {
  homeMenu: Array<HomeMenuType>;
  branchDetail?: BranchInfo | null;
  branchId?: string;
  chatCount?: string;
  noticeCount?: string;
  branchFollowStatus?: string | "0";
  contactList?: Array<string> | undefined;
  socialLinks?: Array<SocialType> | undefined;
  //appLinks?: Array<AppType> | [];
  getBranchDetail: (branchId?: string) => void;
  handleClickFollowing: (
    branchData: BranchInfo,
    branchFollowStatus: string
  ) => void;
  followingProcessing: boolean;
};

export type HomeMenuType = {
  name: string;
  title: string;
  icon: string;
};

export type SocialType = {
  name: string;
  icon: string;
  link: string;
};

/* type AppType = {
  name: string;
  icon: string;
  link: string;
}; */

const HomeContext = createContext<HomeContextType | undefined>(undefined);

export const HomeContextProvider = ({ children }: { children: ReactNode }) => {
  const homeMenu: Array<HomeMenuType> = [
    { name: "chats", title: "dept_chats", icon: "dept_chats" },
    { name: "team", title: "team", icon: "team" },
    { name: "groups", title: "groups", icon: "groups" },
    { name: "feedback", title: "feedback", icon: "feedback" },
    { name: "products", title: "products", icon: "products" },
    { name: "email", title: "email", icon: "email" },
    { name: "app", title: "app", icon: "app_ic" },
    { name: "call", title: "call", icon: "call" },
    { name: "location", title: "location", icon: "location" },
  ];

  const branchId: string = CacheStorage.getValue(StorageKeys.branch_id);

  const [branchDetail, setBranchDetail] = useState<BranchInfo | null>(null);
  const [chatCount, setChatCount] = useState<string>("0");
  const [noticeCount, setNoticeCount] = useState<string>("0");
  const [branchFollowStatus, setBranchFollowStatus] = useState<string>("0");
  const [contactList, setContactList] = useState<Array<string> | undefined>(
    undefined
  );
  const [socialLinks, setSocialLinks] = useState<Array<SocialType> | undefined>(
    undefined
  );

  const [followingProcessing, setFollowingProcessing] =
    useState<boolean>(false);

  //const [appLinks, setAppLinks] = useState<Array<AppType>>([]);

  const handleClickFollowing = async (
    branchData: BranchInfo,
    branchFollowStatus: string = "0"
  ) => {
    const bId: string = branchData?.branch_id ?? "0";
    const groupStatus: string = branchData?.branch_follow_group_status ?? "0";

    if (UtilityHelper.isNotEmpty(bId)) {
      if (groupStatus === "0") {
        if (branchFollowStatus === "1") {
          AlertConfirm({
            title: "are_you_sure_you_wish_to_unfollow_this_branch",
            onConfirm: async () => {
              applyFollowing(bId);
            },
          });
        } else {
          applyFollowing(bId);
        }
      } else {
        // NAVIGATE
        HomeProvider.navigate(appRoutes.routeNameBranchFollowGroup);
      }
    }
  };

  const applyFollowing = async (bId: string) => {
    setFollowingProcessing(true);
    const followStatusResult: boolean =
      await HomeProvider.updateFollowStatusAPI(bId);
    setBranchFollowStatus(followStatusResult ? "1" : "0");
    setFollowingProcessing(false);
  };

  const getBrancghDetailSession = (): boolean => {
    const branchDetailSession: BranchInfo | null = UtilityHelper.isNotEmpty(
      SessionStorage.getValue(StorageKeys.home_branch_detail) || ""
    )
      ? JSON.parse(SessionStorage.getValue(StorageKeys.home_branch_detail))
      : null;

    setupBranchInfo(branchDetailSession!);

    return branchDetailSession != null;
  };

  const getBranchDetail = async (branchId: string = "") => {
    getBrancghDetailSession();

    if (UtilityHelper.isNotEmpty(branchId)) {
      Loader.modal();
    }

    const branchInfoResult = await HomeProvider.getBranchDetail(branchId);
    if (UtilityHelper.isNotEmpty(branchId)) {
      Loader.modal(false);
    }

    if (branchInfoResult != null) {
      const branchInfo: BranchInfo = branchInfoResult?.result?.branch_info!;
      setupBranchInfo(branchInfo);
    }
  };

  const setupBranchInfo = (branchInfo: BranchInfo | null) => {
    if (branchInfo != null) {
      const contactNumber: string = UtilityHelper.isNotEmpty(
        branchInfo?.phone_number!
      )
        ? `${branchInfo?.phone_code!}${branchInfo?.phone_number!}`
        : "";

      const chatNotification: string =
        branchInfo?.menu_notification_details?.chat_notification!;
      const noticeNotification: string =
        branchInfo?.menu_notification_details?.notice_notification!;
      const branchTitle: string = branchInfo?.title!;
      const branchLogo: string = branchInfo?.branch_logo_url!;

      //let apps: Array<AppType> = [];
      let socials: Array<SocialType> = [];

      if (UtilityHelper.isNotEmpty(branchInfo?.website!.trim())) {
        socials.push({
          name: "website",
          icon: "globe",
          link: branchInfo?.website!.trim(),
        });
      }

      (branchInfo.social_media_links ?? []).forEach((item) => {
        socials.push({
          name: item.key!,
          icon: item.key!,
          link: `${item.key!}.com/${item.value!}`,
        });
      });

      /* (branchInfo.app_links ?? []).forEach((item) => {
      apps.push({
        name: item.key!,
        icon: item.key!,
        link: `${item.key!}.com/${item.value!}`,
      });
    }); */

      SessionStorage.saveValue(
        StorageKeys.home_branch_detail,
        JSON.stringify(branchInfo)
      );

      setBranchDetail(branchInfo);
      setChatCount(chatNotification);
      setNoticeCount(noticeNotification);
      setBranchFollowStatus(branchInfo?.branch_follow_status!);
      setContactList([contactNumber]);
      //setAppLinks(apps);
      setSocialLinks(socials);

      CacheStorage.saveValue(StorageKeys.branch_name, branchTitle);
      CacheStorage.saveValue(StorageKeys.branch_logo, branchLogo);

      SessionStorage.saveValue(
        StorageKeys.home_chat_badge_counter,
        chatNotification
      );

      SessionStorage.saveValue(
        StorageKeys.home_notice_badge_counter,
        noticeNotification
      );

      SessionStorage.saveValue(
        StorageKeys.home_branch_product_url,
        branchInfo?.branch_products_details?.status! === "1"
          ? branchInfo?.branch_products_details?.media_details?.file_url!
          : ""
      );
    }
  };

  /* useEffect(() => {
    init();
  }, []); */

  return (
    <HomeContext.Provider
      value={{
        homeMenu,
        branchDetail,
        branchId,
        chatCount,
        noticeCount,
        branchFollowStatus,
        contactList,
        socialLinks,
        getBranchDetail,
        handleClickFollowing,
        followingProcessing,
        //appLinks,
      }}
    >
      {children}
    </HomeContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const HomeContextImport = () => {
  const context = useContext(HomeContext);
  if (!context) {
    throw new Error(
      "HomeContextImport must be used within a HomeContextProvider"
    );
  }
  return context;
};
